import { useEffect, useState, useRef } from "react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Modal from "../components/Modal";
import { toast } from "sonner";
import { LogIn, LogOut, CheckCircle2, AlertTriangle, UserX, Clock, CalendarRange, FileDown, Camera, Image as ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);

const statusMeta = {
  hadir: { label: "Hadir", color: "var(--masuk)" },
  telat: { label: "Telat", color: "var(--keluar)" },
  telat_berat: { label: "Telat Berat", color: "var(--retur)" },
  tidak: { label: "Tidak Masuk", color: "var(--retur)" },
};

export default function Attendance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isOwner = user?.is_owner;
  const [me, setMe] = useState(null);
  const [date, setDate] = useState(today());
  const [report, setReport] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [monthly, setMonthly] = useState(null);

  const [camOpen, setCamOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const loadMe = () => api.get("/attendance/me/today").then((r) => setMe(r.data)).catch(() => {});
  const loadReport = () => { if (isAdmin) api.get(`/attendance?date=${date}`).then((r) => setReport(r.data)).catch((e) => toast.error(errMsg(e))); };
  const loadMonthly = () => { if (isAdmin) api.get(`/attendance/monthly?month=${month}`).then((r) => setMonthly(r.data)).catch((e) => toast.error(errMsg(e))); };

  useEffect(() => { loadMe(); }, []);
  useEffect(() => { loadReport(); }, [date]);
  useEffect(() => { loadMonthly(); }, [month]);

  // start/stop camera when modal toggles
  useEffect(() => {
    let active = true;
    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
        if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (e) {
        toast.error("Tidak bisa mengakses kamera. Izinkan kamera di browser.");
        setCamOpen(false);
      }
    };
    if (camOpen) start();
    return () => {
      active = false;
      if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    };
  }, [camOpen]);

  const closeCam = () => { setCamOpen(false); };

  const capture = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) { toast.error("Kamera belum siap"); return; }
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth; canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.6);
    setBusy(true);
    try {
      await api.post("/attendance/checkin", { photo: dataUrl });
      toast.success("Absen masuk tercatat");
      closeCam(); loadMe(); loadReport();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const checkout = async () => { try { await api.post("/attendance/checkout"); toast.success("Absen pulang tercatat"); loadMe(); loadReport(); } catch (e) { toast.error(errMsg(e)); } };

  const viewPhoto = async (id) => {
    try {
      const res = await fetch(`${API}/attendance/photo/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      if (!res.ok) throw new Error("Gagal memuat foto");
      const blob = await res.blob();
      setPhotoUrl(URL.createObjectURL(blob));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const rows = report ? [
    ...report.present.map((p) => ({ id: p.id, name: p.user_name, status: p.status, masuk: p.check_in, pulang: p.check_out || "-", photo_path: p.photo_path })),
    ...report.absent.map((a) => ({ id: null, name: a.user_name, status: "tidak", masuk: "-", pulang: "-", photo_path: null })),
  ] : [];

  const cols = isOwner ? 5 : 4;

  return (
    <div className="space-y-5 animate-fadeup">
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-extrabold">Absensi</h1>
        <p className="text-[#52525B] text-sm">Catat jam masuk & pulang (foto selfie saat masuk)</p>
      </div>

      {/* My attendance */}
      <div className="nb-card p-5" data-testid="my-attendance">
        <span className="label-tag">Absen Hari Ini — {user?.name}</span>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div className="border-2 border-[#0A0A0A] rounded-lg p-3 text-center">
            <div className="label-tag">Jam Masuk</div>
            <div className="font-display font-extrabold text-2xl mt-1">{me?.check_in || "--:--"}</div>
            {me?.status && <span className="label-tag" style={{ color: statusMeta[me.status]?.color }}>{statusMeta[me.status]?.label}</span>}
          </div>
          <div className="border-2 border-[#0A0A0A] rounded-lg p-3 text-center">
            <div className="label-tag">Jam Pulang</div>
            <div className="font-display font-extrabold text-2xl mt-1">{me?.check_out || "--:--"}</div>
          </div>
        </div>
        <div className="mt-4">
          {!me ? (
            <button data-testid="checkin-button" onClick={() => setCamOpen(true)} className="nb-btn w-full py-3 flex items-center justify-center gap-2 text-[#0A0A0A]" style={{ background: "var(--masuk)" }}>
              <Camera className="w-5 h-5" strokeWidth={2.5} /> Absen Masuk (Ambil Foto)
            </button>
          ) : !me.check_out ? (
            <button data-testid="checkout-button" onClick={checkout} className="nb-btn w-full py-3 flex items-center justify-center gap-2 text-[#0A0A0A]" style={{ background: "var(--keluar)" }}>
              <LogOut className="w-5 h-5" strokeWidth={2.5} /> Absen Pulang
            </button>
          ) : (
            <div className="text-center font-bold flex items-center justify-center gap-2" style={{ color: "var(--masuk)" }}>
              <CheckCircle2 className="w-5 h-5" /> Absensi hari ini selesai
            </div>
          )}
        </div>
      </div>

      {/* Admin recap */}
      {isAdmin && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-display font-bold text-lg flex items-center gap-2"><Clock className="w-5 h-5" /> Rekap Absensi</h2>
            <input data-testid="attendance-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="nb-input w-auto" />
          </div>

          {report && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 items-start">
              <div className="nb-card p-4 text-center" style={{ background: "var(--masuk)" }}><CheckCircle2 className="w-5 h-5 mx-auto" /><div className="font-display font-extrabold text-2xl">{report.summary.hadir}</div><span className="label-tag">Hadir</span></div>
              <div className="nb-card p-4 text-center" style={{ background: "var(--keluar)" }}><AlertTriangle className="w-5 h-5 mx-auto" /><div className="font-display font-extrabold text-2xl">{report.summary.telat}</div><span className="label-tag">Telat (kuning)</span></div>
              <div className="nb-card p-4 text-center" style={{ background: "var(--retur)" }}><AlertTriangle className="w-5 h-5 mx-auto" /><div className="font-display font-extrabold text-2xl">{report.summary.telat_berat}</div><span className="label-tag">Telat Berat</span></div>
              <div className="nb-card p-4 text-center" style={{ background: "var(--retur)" }}><UserX className="w-5 h-5 mx-auto" /><div className="font-display font-extrabold text-2xl">{report.summary.tidak_masuk}</div><span className="label-tag">Tidak Masuk</span></div>
            </div>
          )}

          <div className="nb-card p-0 overflow-x-auto" data-testid="attendance-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-[#0A0A0A]" style={{ background: "var(--primary)" }}>
                  {["Nama", "Status", "Masuk", "Pulang"].map((h) => <th key={h} className="text-left font-bold px-3 py-2">{h}</th>)}
                  {isOwner && <th className="text-left font-bold px-3 py-2">Foto</th>}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={cols} className="text-center text-[#52525B] py-8">Belum ada data.</td></tr>
                ) : rows.map((r, idx) => (
                  <tr key={idx} className="border-b border-[#0A0A0A]/20">
                    <td className="px-3 py-2 font-bold">{r.name}</td>
                    <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-md text-xs font-bold border-2 border-[#0A0A0A]" style={{ background: statusMeta[r.status]?.color }}>{statusMeta[r.status]?.label}</span></td>
                    <td className="px-3 py-2">{r.masuk}</td>
                    <td className="px-3 py-2">{r.pulang}</td>
                    {isOwner && (
                      <td className="px-3 py-2">
                        {r.photo_path ? (
                          <button data-testid={`view-photo-${r.id}`} onClick={() => viewPhoto(r.id)} className="nb-btn px-2 py-1 bg-white text-xs flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Lihat</button>
                        ) : <span className="text-xs text-[#52525B]">—</span>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
            <h2 className="font-display font-bold text-lg flex items-center gap-2"><CalendarRange className="w-5 h-5" /> Rekap Bulanan per Pegawai</h2>
            <div className="flex items-center gap-2">
              <input data-testid="monthly-attendance-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="nb-input w-auto" />
              <button data-testid="monthly-attendance-excel" onClick={() => downloadMonthly("excel")} className="nb-btn px-3 py-2 bg-white text-sm flex items-center gap-1"><FileDown className="w-4 h-4" /> Excel</button>
              <button data-testid="monthly-attendance-pdf" onClick={() => downloadMonthly("pdf")} className="nb-btn px-3 py-2 text-sm flex items-center gap-1 text-[#0A0A0A]" style={{ background: "var(--primary)" }}><FileDown className="w-4 h-4" /> PDF</button>
            </div>
          </div>
          <div className="nb-card p-0 overflow-x-auto" data-testid="monthly-attendance-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-[#0A0A0A]" style={{ background: "var(--primary)" }}>
                  {["Nama", "Hadir", "Telat", "Telat Berat", "Total Hari Hadir"].map((h) => <th key={h} className="text-left font-bold px-3 py-2 whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {(!monthly || monthly.rows.length === 0) ? (
                  <tr><td colSpan={5} className="text-center text-[#52525B] py-8">Belum ada data.</td></tr>
                ) : monthly.rows.map((r, idx) => (
                  <tr key={idx} className="border-b border-[#0A0A0A]/20">
                    <td className="px-3 py-2 font-bold">{r.user_name}</td>
                    <td className="px-3 py-2">{r.hadir}</td>
                    <td className="px-3 py-2">{r.telat}</td>
                    <td className="px-3 py-2">{r.telat_berat}</td>
                    <td className="px-3 py-2 font-bold">{r.hari_hadir}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Camera modal */}
      <Modal open={camOpen} onClose={closeCam} title="Ambil Foto Absen Masuk" testid="camera-modal">
        <div className="space-y-3">
          <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-lg border-2 border-[#0A0A0A] bg-black" style={{ transform: "scaleX(-1)", maxHeight: 360 }} />
          <button data-testid="capture-photo-button" onClick={capture} disabled={busy} className="nb-btn w-full py-3 flex items-center justify-center gap-2 text-[#0A0A0A] disabled:opacity-60" style={{ background: "var(--masuk)" }}>
            <Camera className="w-5 h-5" strokeWidth={2.5} /> {busy ? "Menyimpan…" : "Ambil Foto & Absen"}
          </button>
          <p className="text-xs text-[#52525B] text-center">Foto wajib untuk mencatat absen masuk.</p>
        </div>
      </Modal>

      {/* Photo viewer (owner) */}
      <Modal open={!!photoUrl} onClose={() => { setPhotoUrl(null); }} title="Foto Absensi" testid="photo-modal">
        {photoUrl && <img src={photoUrl} alt="Foto absensi" className="w-full rounded-lg border-2 border-[#0A0A0A]" />}
      </Modal>
    </div>
  );

  function downloadMonthly(fmt) {
    const kind = fmt === "pdf" ? "attendance" : "attendance_monthly";
    fetch(`${API}/reports/export?kind=${kind}&month=${month}&fmt=${fmt}`, {
      credentials: "include", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    }).then(async (res) => {
      if (!res.ok) throw new Error("Gagal mengunduh");
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `rekap_absensi_${month}.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click(); URL.revokeObjectURL(url);
      toast.success("Rekap diunduh");
    }).catch((e) => toast.error(errMsg(e)));
  }
}
